import { useState, useEffect, useRef, useCallback } from 'react'
import {
  User, ArrowUpRight, Slash, Square, Type, Triangle,
  Trash2, Undo2, Redo2, Download, Save, Pencil, Check,
  ChevronDown, Spline, MoveUpRight, MousePointer2, AlertCircle, RotateCw,
} from 'lucide-react'
import {
  parseAnnotationSets, findAnnotationSetAt, upsertAnnotationSet,
} from '../../utils/annotationSets'
import {
  VB_W, VB_H, BLUE, YELLOW, VIOLET, WHITE, COLORS,
  colorId, curveControl, AnnotationDefs, renderElements,
  haloRy, haloRx, haloAnchor, HALO_MIN_RX, HALO_MAX_RX, useStrokeScale,
} from './AnnotationLayer'

// Mode annotation V1 (style Once Video Analyser simplifie) : couche SVG posee
// sur la frame video mise en pause. 6 outils : halo, fleche, ligne/passe,
// zone hachuree, texte, ligne tactique (entre halos). Coordonnees normalisees
// 0-1 -> rendues dans un viewBox 1600x900 (preserveAspectRatio none).
//
// NB : on recoit playerRef (imperative handle) et non un videoRef brut. Pour
// l'export PNG on utilise playerRef.captureFrame() qui rend la frame courante.
//
// PERSISTANCE — le dessin est un jeu { t, elements } dans la colonne JSONB
// video_moments.annotations, via des routes qui existent :
//   - moment fourni  -> PATCH /api/moments/{id}/       (champ annotations)
//   - pas de moment  -> POST  /api/matches/{id}/moments/ (cree + annote en un appel)
// Le tableau complet est renvoye a chaque fois, jeu courant insere ou remplace
// a son t, trie par t croissant.
//
// REGLE, issue du bug d'origine (PR #190-194) : onSuccess() et setDirty(false)
// ne doivent JAMAIS etre appeles sur un chemin d'echec. Le code precedent
// tapait POST /api/matches/{id}/annotations/ — route inexistante — puis
// traitait le 404 comme un succes en basculant sur un localStorage jamais relu.
// Le coach croyait avoir enregistre. Toute erreur laisse desormais l'overlay
// ouvert, dirty a true, et s'affiche dans l'overlay lui-meme.

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

const DISP = "'Barlow Condensed', sans-serif"
const BODY = "'Space Grotesk', sans-serif"
const MONO = "'JetBrains Mono', 'Space Grotesk', monospace"

// Couleur par defaut de chaque outil (design systeme, point 9 du chantier)
const TOOL_DEFAULT_COLOR = {
  halo: BLUE, arrow: BLUE, line: YELLOW, zone: VIOLET, text: WHITE, tactical_line: BLUE,
}

// Outils simples de la barre (la fleche est un dropdown a part, cf. ARROW_STYLES)
const SIMPLE_TOOLS = [
  { id: 'halo', icon: User,   label: 'Halo joueur' },
  { id: 'line', icon: Slash,  label: 'Ligne / passe' },
  { id: 'zone', icon: Square, label: 'Zone' },
  { id: 'text', icon: Type,   label: 'Texte' },
]

// Sous-outils de la fleche (dropdown, pattern Figma/Excalidraw)
const ARROW_STYLES = [
  { id: 'straight', icon: ArrowUpRight, label: 'Flèche droite' },
  { id: 'curved',   icon: Spline,       label: 'Flèche courbe' },
  { id: 'dashed',   icon: MoveUpRight,  label: 'Flèche pointillée' },
]

// ─── Hit-testing (helpers purs, coordonnees libres) ──────────────────────────
function distToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1
  const l2 = dx * dx + dy * dy
  if (l2 === 0) return Math.hypot(px - x1, py - y1)
  let t = ((px - x1) * dx + (py - y1) * dy) / l2
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy))
}

function pointInPolygon(px, py, pts) {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const xi = pts[i].x, yi = pts[i].y, xj = pts[j].x, yj = pts[j].y
    const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)
    if (intersect) inside = !inside
  }
  return inside
}

// Point d'une courbe de Bezier quadratique a l'instant t
function quadPoint(p0, c, p1, t) {
  const mt = 1 - t
  return { x: mt * mt * p0.x + 2 * mt * t * c.x + t * t * p1.x, y: mt * mt * p0.y + 2 * mt * t * c.y + t * t * p1.y }
}

function uid() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
}

function isNetworkError(e) {
  return e instanceof TypeError && /fetch|network/i.test(e.message || '')
}

// Le backend renvoie `detail` en string, ou en objet structure pour les cas
// produit (free_expired, free_quota_reached).
function detailToMessage(detail, fallback) {
  if (!detail) return fallback
  if (typeof detail === 'string') return detail
  if (typeof detail === 'object' && typeof detail.message === 'string') return detail.message
  return fallback
}

async function apiSend(url, method, body) {
  const r = await fetch(url, {
    method, headers: authHeaders(), body: JSON.stringify(body),
  })
  if (!r.ok) {
    const payload = await r.json().catch(() => ({}))
    const err = new Error(detailToMessage(payload.detail, `Erreur ${r.status}`))
    err.status = r.status
    throw err
  }
  return r.json()
}

// Bounding box (normalise) d'une annotation, pour le contour de selection
function bbox(a, haloById) {
  let pts = a.points || []
  if (a.type === 'tactical_line') {
    pts = (a.linkedHalos || []).map(id => haloAnchor(haloById.get(id))).filter(Boolean)
  }
  if (pts.length === 0) return null
  const xs = pts.map(p => p.x), ys = pts.map(p => p.y)
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) }
}

export default function AnnotationOverlay({
  playerRef,
  videoDuration = 0,
  currentTime = 0,
  matchId,
  moment = null,
  onCancel,
  onSuccess,
  onDirtyChange,
  showToast,
}) {
  // Jeu deja enregistre pour cette frame, s'il existe. Les entrees au format
  // herite de mai 2026 sont ecartees par parseAnnotationSets : overlay vide,
  // un console.warn, aucun crash. Elles disparaissent a la sauvegarde.
  const [annotations, setAnnotations] = useState(
    () => findAnnotationSetAt(parseAnnotationSets(moment?.annotations), currentTime)?.elements || [],
  )
  const [tool, setTool] = useState('select')   // outil Selection actif par defaut
  const [grabbing, setGrabbing] = useState(false)
  const [arrowStyle, setArrowStyle] = useState('straight')
  const [arrowMenuOpen, setArrowMenuOpen] = useState(false)
  const [color, setColor] = useState(BLUE)
  const [selectedId, setSelectedId] = useState(null)
  const [undoStack, setUndoStack] = useState([])
  const [redoStack, setRedoStack] = useState([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)

  // Etats de dessin en cours
  const [draftDrag, setDraftDrag] = useState(null)   // { type, color, a:{x,y}, b:{x,y} }
  const [draftPoly, setDraftPoly] = useState([])     // points de zone en cours
  const [draftTactical, setDraftTactical] = useState(null) // { color, linkedHalos:[] }
  const [textDraft, setTextDraft] = useState(null)   // { x, y, value }

  const rootRef = useRef(null)
  const svgRef = useRef(null)
  // Epaisseurs de trait constantes a l'ecran, quelle que soit la taille du
  // player. Meme mecanisme et meme source que la couche de lecture.
  const k = useStrokeScale(svgRef)
  const draggingRef = useRef(false)
  const arrowMenuRef = useRef(null)
  const dragSelRef = useRef(null)   // drag de l'outil Selection (move/resize/control)

  const haloById = new Map(annotations.filter(a => a.type === 'halo').map(a => [a.id, a]))

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Le parent porte la confirmation de sortie : le bouton "Quitter
  // l'annotation" du header et la touche Echap doivent passer par le meme
  // garde-fou, et seul VideoStudio voit les deux.
  useEffect(() => {
    onDirtyChange && onDirtyChange(dirty)
  }, [dirty, onDirtyChange])

  // Ferme le dropdown fleche au clic exterieur
  useEffect(() => {
    if (!arrowMenuOpen) return
    const onDown = (e) => {
      if (arrowMenuRef.current && !arrowMenuRef.current.contains(e.target)) setArrowMenuOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [arrowMenuOpen])

  // ─── Historique ────────────────────────────────────────────────────────────
  const commit = useCallback((next) => {
    setUndoStack(s => [...s, annotations])
    setRedoStack([])
    setAnnotations(next)
    setDirty(true)
    setSaveError(null)   // le coach repart au dessin : l'erreur precedente n'a plus d'objet
  }, [annotations])

  const undo = () => {
    if (undoStack.length === 0) return
    const prev = undoStack[undoStack.length - 1]
    setRedoStack(r => [...r, annotations])
    setUndoStack(s => s.slice(0, -1))
    setAnnotations(prev)
    setDirty(true)
  }
  const redo = () => {
    if (redoStack.length === 0) return
    const next = redoStack[redoStack.length - 1]
    setUndoStack(s => [...s, annotations])
    setRedoStack(r => r.slice(0, -1))
    setAnnotations(next)
    setDirty(true)
  }

  // ─── Coordonnees ────────────────────────────────────────────────────────────
  const getPoint = (e) => {
    const el = svgRef.current
    if (!el) return { x: 0, y: 0 }
    const rect = el.getBoundingClientRect()
    const x = rect.width ? (e.clientX - rect.left) / rect.width : 0
    const y = rect.height ? (e.clientY - rect.top) / rect.height : 0
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) }
  }

  // ─── Selection : hit-test + poignees (coordonnees viewBox) ───────────────────
  const HIT_SEG = 16       // tolerance de clic sur un trait
  const HANDLE_HIT = 22    // rayon de clic d'une poignee
  // Marge de clic autour de l'empreinte, et plancher pour les petits halos :
  // une ellipse de 12 unites de large ne se vise pas au doigt sans aide.
  const HALO_HIT_PAD = 6
  const HALO_HIT_MIN_RX = 30
  const HALO_HIT_MIN_RY = 14

  const haloCenter = (id) => {
    const c = haloAnchor(haloById.get(id))
    return c ? { x: c.x * VB_W, y: c.y * VB_H } : null
  }
  const annVb = (a) => a.points.map(p => ({ x: p.x * VB_W, y: p.y * VB_H }))

  const hitTestAnnotation = (a, vx, vy) => {
    if (a.type === 'halo') {
      // ANNOT-HITTEST-HALO-001. La zone de clic etait deux constantes en dur
      // (34 et 13) sans lien avec la taille du halo : agrandi a rx = 260, il
      // devenait inselectionnable sur presque toute sa surface. Elle epouse
      // desormais l'ellipse DESSINEE — meme centre decale, memes demi-axes —
      // avec une marge et un plancher pour rester atteignable au doigt.
      const c = haloAnchor(a)
      const rx = haloRx(a)
      const dx = (vx - c.x * VB_W) / Math.max(rx + HALO_HIT_PAD, HALO_HIT_MIN_RX)
      const dy = (vy - c.y * VB_H) / Math.max(haloRy(rx) + HALO_HIT_PAD, HALO_HIT_MIN_RY)
      return dx * dx + dy * dy <= 1
    }
    if (a.type === 'text') {
      const x = a.points[0].x * VB_W, y = a.points[0].y * VB_H
      const w = Math.max(60, (a.text?.length || 1) * 18)
      return vx >= x - w / 2 && vx <= x + w / 2 && vy >= y - 42 && vy <= y + 12
    }
    if (a.type === 'zone') return pointInPolygon(vx, vy, annVb(a))
    if (a.type === 'tactical_line') {
      const pts = (a.linkedHalos || []).map(haloCenter).filter(Boolean)
      for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length
        if (distToSegment(vx, vy, pts[i].x, pts[i].y, pts[j].x, pts[j].y) < HIT_SEG) return true
      }
      return false
    }
    if (a.type === 'arrow' && a.arrow_style === 'curved') {
      const p0 = { x: a.points[0].x * VB_W, y: a.points[0].y * VB_H }
      const p1 = { x: a.points[1].x * VB_W, y: a.points[1].y * VB_H }
      const cc = a.control || curveControl(a.points[0], a.points[1])
      const c = { x: cc.x * VB_W, y: cc.y * VB_H }
      let prev = p0
      for (let t = 0.1; t <= 1.0001; t += 0.1) {
        const q = quadPoint(p0, c, p1, t)
        if (distToSegment(vx, vy, prev.x, prev.y, q.x, q.y) < HIT_SEG) return true
        prev = q
      }
      return false
    }
    // arrow straight/dashed + line
    const p0 = a.points[0], p1 = a.points[1]
    return distToSegment(vx, vy, p0.x * VB_W, p0.y * VB_H, p1.x * VB_W, p1.y * VB_H) < HIT_SEG
  }

  const pickAnnotation = (vx, vy) => {
    for (let i = annotations.length - 1; i >= 0; i--) {
      if (hitTestAnnotation(annotations[i], vx, vy)) return annotations[i]
    }
    return null
  }

  // Poignees d'une annotation (viewBox). Texte/tactique = deplacement seul.
  const handlesOf = (a) => {
    if (a.type === 'halo') {
      const rx = haloRx(a)
      const c = haloAnchor(a)
      const cx = c.x * VB_W, cy = c.y * VB_H
      return [
        { mode: 'halo-resize', x: cx - rx - 8, y: cy },
        { mode: 'halo-resize', x: cx + rx + 8, y: cy },
      ]
    }
    if (a.type === 'arrow' || a.type === 'line') {
      const hs = [
        { mode: 'end', index: 0, x: a.points[0].x * VB_W, y: a.points[0].y * VB_H },
        { mode: 'end', index: 1, x: a.points[1].x * VB_W, y: a.points[1].y * VB_H },
      ]
      if (a.type === 'arrow' && a.arrow_style === 'curved') {
        const cc = a.control || curveControl(a.points[0], a.points[1])
        hs.push({ mode: 'control', x: cc.x * VB_W, y: cc.y * VB_H })
      }
      return hs
    }
    if (a.type === 'zone') return a.points.map((p, i) => ({ mode: 'vertex', index: i, x: p.x * VB_W, y: p.y * VB_H }))
    return []
  }

  const pickHandle = (a, vx, vy) => {
    for (const h of handlesOf(a)) {
      if (Math.hypot(vx - h.x, vy - h.y) <= HANDLE_HIT) return h
    }
    return null
  }

  const pushUndo = () => { setUndoStack(s => [...s, annotations]); setRedoStack([]); setDirty(true) }

  // On NE capture PAS le pointeur sur le <svg> : le pointerdown vise souvent un
  // enfant (ellipse du halo, etc.) et capturer sur un ancetre ne redirige pas
  // les pointermove de facon fiable. On ecoute donc window pendant le drag
  // (cf. l'effet ci-dessous), pattern robuste et independant du re-render.
  const beginSelDrag = (a, mode, index, p, wasSelected = false) => {
    dragSelRef.current = {
      mode, index, annId: a.id, startP: p, moved: false, wasSelected,
      orig: { points: a.points.map(pt => ({ ...pt })), control: a.control ? { ...a.control } : undefined, rx: a.rx },
    }
    draggingRef.current = true
    setGrabbing(true)
  }

  const applySelDrag = (p) => {
    const ds = dragSelRef.current
    if (!ds) return
    if (!ds.moved) { ds.moved = true; pushUndo() }   // snapshot au 1er mouvement
    const dx = p.x - ds.startP.x, dy = p.y - ds.startP.y
    setAnnotations(prev => prev.map(a => {
      if (a.id !== ds.annId) return a
      if (ds.mode === 'move') {
        const out = { ...a, points: ds.orig.points.map(pt => ({ x: pt.x + dx, y: pt.y + dy })) }
        if (ds.orig.control) out.control = { x: ds.orig.control.x + dx, y: ds.orig.control.y + dy }
        return out
      }
      if (ds.mode === 'end' || ds.mode === 'vertex') {
        return { ...a, points: a.points.map((pt, i) => (i === ds.index ? { x: p.x, y: p.y } : pt)) }
      }
      if (ds.mode === 'control') return { ...a, control: { x: p.x, y: p.y } }
      if (ds.mode === 'halo-resize') {
        const cxVb = a.points[0].x * VB_W
        let rx = Math.abs(p.x * VB_W - cxVb) - 8
        rx = Math.max(HALO_MIN_RX, Math.min(HALO_MAX_RX, rx))
        return { ...a, rx }
      }
      return a
    }))
  }

  // Drag de selection pilote par window (move/up), robuste vs capture SVG.
  useEffect(() => {
    if (!grabbing) return
    const onMove = (ev) => { if (dragSelRef.current) applySelDrag(getPoint(ev)) }
    const onUp = () => {
      const ds = dragSelRef.current
      // Retaper un libelle deja selectionne, sans le deplacer, rouvre sa
      // saisie. Le double-clic fait la meme chose a la souris ; ce geste-ci est
      // celui qui existe au doigt, ou il n'y a pas de double-clic.
      //
      // Le garde `!ds.moved` protege aussi la lecture de `annotations` : cette
      // closure date du debut du drag, elle n'est fiable que si rien n'a bouge.
      if (ds && ds.mode === 'move' && ds.wasSelected && !ds.moved) {
        const a = annotations.find(x => x.id === ds.annId)
        if (a && a.type === 'text') beginTextEdit(a)
      }
      dragSelRef.current = null; draggingRef.current = false; setGrabbing(false)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grabbing])

  // ─── Outils : surface de dessin ──────────────────────────────────────────────
  const onSurfaceDown = (e) => {
    const p = getPoint(e)

    // Outil Selection : poignee > corps d'annotation > vide (deselection)
    if (tool === 'select') {
      const vx = p.x * VB_W, vy = p.y * VB_H
      if (selectedId) {
        const cur = annotations.find(x => x.id === selectedId)
        const h = cur && pickHandle(cur, vx, vy)
        if (h) { beginSelDrag(cur, h.mode, h.index, p); return }
      }
      const hit = pickAnnotation(vx, vy)
      if (hit) {
        const wasSelected = selectedId === hit.id
        setSelectedId(hit.id)
        beginSelDrag(hit, 'move', undefined, p, wasSelected)
        return
      }
      setSelectedId(null)
      return
    }

    // Outils de dessin : un clic dans le vide deselectionne
    setSelectedId(null)
    if (tool === 'halo') {
      commit([...annotations, { id: uid(), type: 'halo', color, points: [p] }])
    } else if (tool === 'arrow' || tool === 'line') {
      draggingRef.current = true
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch { /* noop */ }
      setDraftDrag({ type: tool, color, a: p, b: p, arrowStyle: tool === 'arrow' ? arrowStyle : undefined })
    } else if (tool === 'zone') {
      setDraftPoly(prev => [...prev, p])
    } else if (tool === 'text') {
      setTextDraft({ x: p.x, y: p.y, value: '' })
    } else if (tool === 'tactical_line') {
      // clic dans le vide : on ne fait rien (on attend des clics sur les halos)
    }
  }

  // Le drag de l'outil Selection est gere par les listeners window (cf. effet).
  // Ici on ne traite que le dessin (draftDrag des fleches / lignes).
  const onSurfaceMove = (e) => {
    if (!draggingRef.current || !draftDrag) return
    const p = getPoint(e)
    setDraftDrag(d => d ? { ...d, b: p } : d)
  }

  const onSurfaceUp = (e) => {
    if (draggingRef.current && draftDrag) {
      draggingRef.current = false
      try { e.currentTarget.releasePointerCapture(e.pointerId) } catch { /* noop */ }
      const { type, color: c, a, b, arrowStyle: aStyle } = draftDrag
      setDraftDrag(null)
      // ignore les micro-drags
      const d = Math.hypot(b.x - a.x, b.y - a.y)
      if (d > 0.01) {
        if (type === 'arrow') {
          const style = aStyle || 'straight'
          const ann = { id: uid(), type: 'arrow', arrow_style: style, color: c, points: [a, b] }
          if (style === 'curved') ann.control = curveControl(a, b)
          commit([...annotations, ann])
        } else {
          commit([...annotations, { id: uid(), type, color: c, points: [a, b] }])
        }
      }
    }
  }

  const finishZone = () => {
    if (draftPoly.length >= 3) {
      commit([...annotations, { id: uid(), type: 'zone', color, points: draftPoly }])
    }
    setDraftPoly([])
  }

  const finishTactical = () => {
    if (draftTactical && draftTactical.linkedHalos.length >= 2) {
      commit([...annotations, { id: uid(), type: 'tactical_line', color: draftTactical.color, points: [], linkedHalos: draftTactical.linkedHalos }])
    }
    setDraftTactical(null)
  }

  // Clic sur un halo : uniquement pour la ligne tactique (ajout au linkedHalos).
  // Pour les autres outils on laisse l'evenement remonter a la surface (dessin
  // ou selection via hit-test central).
  const onHaloDown = (a) => (e) => {
    if (tool === 'tactical_line') {
      e.stopPropagation()
      setDraftTactical(prev => {
        const base = prev || { color, linkedHalos: [] }
        if (base.linkedHalos.includes(a.id)) return base
        return { ...base, linkedHalos: [...base.linkedHalos, a.id] }
      })
    }
  }

  // Rouvre la saisie sur un libelle existant. `id` est ce qui distingue une
  // correction d'une creation : sans lui, commitText ajoute une forme de plus
  // et l'ancienne reste dessous — c'etait le seul attribut d'une annotation
  // qu'on ne pouvait pas corriger, il fallait l'effacer et le refaire.
  const beginTextEdit = (a) => {
    setSelectedId(a.id)
    setTextDraft({ id: a.id, x: a.points[0].x, y: a.points[0].y, value: a.text || '' })
  }

  const commitText = () => {
    if (!textDraft) return
    const value = textDraft.value.trim()
    if (textDraft.id) {
      // Rouvrir un libelle pour le relire, puis valider sans y toucher, ne
      // modifie rien : ni pile d'annulation, ni `dirty`. Sinon le coach se voit
      // demander s'il accepte de perdre un dessin qu'il n'a pas change.
      const avant = annotations.find(a => a.id === textDraft.id)
      if (avant && avant.text === value) { setTextDraft(null); return }
      // Correction : meme id, meme position, seul le texte change. Vider le
      // champ efface le libelle — un texte vide ne se voit pas mais reste
      // selectionnable et cliquable, ce serait un piege sur l'image.
      commit(value
        ? annotations.map(a => (a.id === textDraft.id ? { ...a, text: value } : a))
        : annotations.filter(a => a.id !== textDraft.id))
      if (!value) setSelectedId(null)
    } else if (value) {
      commit([...annotations, { id: uid(), type: 'text', color: WHITE, points: [{ x: textDraft.x, y: textDraft.y }], text: value }])
    }
    setTextDraft(null)
  }

  const deleteSelected = () => {
    if (!selectedId) return
    commit(annotations.filter(a => a.id !== selectedId))
    setSelectedId(null)
  }

  const clearAll = () => {
    if (annotations.length === 0) return
    if (!window.confirm('Effacer toutes les annotations ?')) return
    commit([])
    setSelectedId(null)
  }

  const selectTool = (t) => {
    setTool(t)
    if (TOOL_DEFAULT_COLOR[t]) setColor(TOOL_DEFAULT_COLOR[t])   // pas de reset pour 'select'
    setDraftPoly([]); setDraftTactical(null); setTextDraft(null)
    setArrowMenuOpen(false)
  }

  // Choix d'un sous-style de fleche depuis le dropdown : selectionne l'outil
  // fleche avec ce style et referme le menu.
  const chooseArrowStyle = (style) => {
    setArrowStyle(style)
    setTool('arrow')
    setColor(TOOL_DEFAULT_COLOR.arrow)
    setDraftPoly([]); setDraftTactical(null); setTextDraft(null)
    setArrowMenuOpen(false)
  }

  // ─── Clavier ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey
      if (meta && e.key.toLowerCase() === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return }
      if (meta && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) { e.preventDefault(); redo(); return }
      if (e.key === 'Enter') {
        if (draftPoly.length > 0) { e.preventDefault(); finishZone() }
        else if (draftTactical) { e.preventDefault(); finishTactical() }
        return
      }
      if (e.key === 'Escape') {
        if (textDraft) { setTextDraft(null); return }
        if (draftPoly.length > 0) { setDraftPoly([]); return }
        if (draftTactical) { setDraftTactical(null); return }
        if (selectedId) { setSelectedId(null); return }   // deselectionne d'abord
        handleCancel()
        return
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId) {
        // pas quand on tape dans un input
        if (document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return
        e.preventDefault(); deleteSelected()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoStack, redoStack, annotations, draftPoly, draftTactical, textDraft, selectedId, tool, color])

  // ─── Export PNG (canvas manuel : frame + SVG) ────────────────────────────────
  const drawUrl = (ctx, url) => new Promise((resolve) => {
    const img = new Image()
    img.onload = () => { ctx.drawImage(img, 0, 0, VB_W, VB_H); resolve() }
    img.onerror = () => resolve()
    img.src = url
  })

  const exportPNG = async () => {
    try {
      const frame = playerRef?.current?.captureFrame?.() || null
      const canvas = document.createElement('canvas')
      canvas.width = VB_W; canvas.height = VB_H
      const ctx = canvas.getContext('2d')
      if (frame) await drawUrl(ctx, frame)
      else { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, VB_W, VB_H) }

      if (svgRef.current) {
        const clone = svgRef.current.cloneNode(true)
        // L'export est une TROISIEME surface de rendu, et c'etait la seule a
        // diverger : elle serialise le SVG de l'editeur tel quel. Sans ce
        // retrait, le contour de selection en pointille bleu et ses poignees
        // rondes se retrouvaient graves dans l'image envoyee aux joueurs.
        clone.querySelectorAll('[data-chrome]').forEach(n => n.remove())
        clone.setAttribute('width', VB_W)
        clone.setAttribute('height', VB_H)
        const xml = new XMLSerializer().serializeToString(clone)
        const svgUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`
        await drawUrl(ctx, svgUrl)
      }

      const png = canvas.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = png
      a.download = `annotation-${matchId}-${Math.floor(currentTime)}.png`
      document.body.appendChild(a); a.click(); a.remove()
    } catch (e) {
      showToast && showToast('Export PNG impossible (frame non accessible)', 'error')
    }
  }

  // ─── Sauvegarde backend ──────────────────────────────────────────────────────
  // Un seul appel dans les deux cas : POST /matches/{id}/moments/ accepte le
  // champ annotations des la creation (create_moment, video_moments.py:295),
  // donc pas de POST-puis-PATCH a rattraper si le second echoue.
  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    setSaveError(null)
    try {
      const nextAnnotations = upsertAnnotationSet(moment?.annotations, currentTime, annotations)
      const saved = moment
        ? await apiSend(`${API}/moments/${moment.id}/`, 'PATCH', { annotations: nextAnnotations })
        : await apiSend(`${API}/matches/${matchId}/moments/`, 'POST', {
            timecode_start: currentTime,
            moment_type: 'tactical_note',
            annotations: nextAnnotations,
          })
      // 2xx confirme : et seulement ici.
      setDirty(false)
      onSuccess && onSuccess(saved)
    } catch (e) {
      setSaveError(isNetworkError(e)
        ? 'Connexion perdue. Ton dessin est intact, réessaie.'
        : (e?.message || 'Échec de l\'enregistrement.'))
    } finally {
      setSaving(false)
    }
  }

  // La confirmation de sortie est portee par le parent (cf. onDirtyChange).
  const handleCancel = () => {
    onCancel && onCancel()
  }

  // ─── Rendu SVG des annotations ───────────────────────────────────────────────
  // Le dessin lui-meme vit dans AnnotationLayer, partage avec le mode lecture :
  // ce qui est enregistre et ce qui est relu sont peints par le meme code.
  // Ici on ne passe que ce qui n'a de sens qu'en edition.
  const X = (v) => v * VB_W
  const Y = (v) => v * VB_H

  const renderOpts = {
    haloById,
    linkingIds: draftTactical ? draftTactical.linkedHalos : null,
    onHaloDown,
    interactive: true,
    k,
  }

  // Overlay de selection : contour bleu pointille + poignees (viewBox)
  // Pas de contour de selection autour du libelle qu'on est en train de saisir :
  // il est masque, le contour flotterait autour de rien.
  const selectedAnn = (selectedId && selectedId !== textDraft?.id)
    ? annotations.find(x => x.id === selectedId)
    : null

  // ─── Styles ──────────────────────────────────────────────────────────────────
  const toolBtn = (active) => ({
    width: isMobile ? 28 : 30, height: isMobile ? 28 : 30, borderRadius: 5,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: active ? BLUE : 'transparent', color: active ? '#fff' : 'rgba(255,255,255,0.85)',
    border: 'none', cursor: 'pointer', flexShrink: 0, touchAction: 'manipulation',
  })
  const divider = { width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.15)', margin: '2px 3px' }
  const actionBtn = (variant) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: isMobile ? '7px 10px' : '7px 12px', borderRadius: 7, cursor: 'pointer',
    fontFamily: BODY, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    background: variant === 'primary' ? BLUE : 'rgba(255,255,255,0.1)',
    color: '#fff', border: variant === 'primary' ? 'none' : '0.5px solid rgba(255,255,255,0.2)',
    minHeight: 40,
  })
  const smallBtn = {
    width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.2)', color: '#fff',
    cursor: 'pointer', flexShrink: 0,
  }

  const surfaceCursor = tool === 'select'
    ? (grabbing ? 'grabbing' : 'default')
    : (tool === 'text' ? 'text' : (tool === 'tactical_line' ? 'pointer' : 'crosshair'))
  const ArrowStyleIcon = (ARROW_STYLES.find(s => s.id === arrowStyle) || ARROW_STYLES[0]).icon

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label="Mode annotation"
      style={{ position: 'absolute', inset: 0, zIndex: 4 }}
    >
      <style>{`
        .ano-btn:focus-visible{outline:2px solid #fff;outline-offset:2px}
        .ano-btn:hover{background:rgba(255,255,255,0.12)}
      `}</style>

      {/* Surface SVG */}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="none"
        onPointerDown={onSurfaceDown}
        onPointerMove={onSurfaceMove}
        onPointerUp={onSurfaceUp}
        onPointerCancel={onSurfaceUp}
        onDoubleClick={(e) => {
          if (draftPoly.length >= 3) { finishZone(); return }
          if (tool !== 'select') return
          const p = getPoint(e)
          const hit = pickAnnotation(p.x * VB_W, p.y * VB_H)
          if (hit && hit.type === 'text') beginTextEdit(hit)
        }}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', touchAction: 'none', cursor: surfaceCursor }}
      >
        <AnnotationDefs k={k} />

        {/* Le libelle en cours de correction est masque : le champ de saisie
            occupe sa place, les voir tous les deux ferait doublon a l'ecran. */}
        {renderElements(annotations.filter(a => a.id !== textDraft?.id), renderOpts)}

        {/* Tout ce qui suit n'existe QUE pendant l'edition : traces en cours,
            contour de selection, poignees. `data-chrome` les designe d'un seul
            tenant pour que l'export PNG les retire — sans ce marquage, exporter
            avec une forme selectionnee gravait le contour bleu pointille et ses
            ronds dans l'image partagee aux joueurs. */}
        <g data-chrome="1">

        {/* Zone en cours. Les traces en cours suivent `k` comme les formes
            enregistrees : c'est le meme objet, avant et apres le relachement.
            Sans ca le trait epaissirait d'un coup en fin de geste, et sur
            mobile le coach dessinerait un trait sous le pixel sans le voir.
            Le contour de SELECTION, lui, n'y touche pas : c'est une affordance
            d'interface, sa lisibilite sur petit ecran est un sujet a part. */}
        {draftPoly.length > 0 && (
          <>
            <polyline points={draftPoly.map(p => `${X(p.x)},${Y(p.y)}`).join(' ')} fill="none" stroke={color} strokeWidth={2.5 * k} strokeDasharray={`${6 * k},${5 * k}`} />
            {draftPoly.map((p, i) => <circle key={i} cx={X(p.x)} cy={Y(p.y)} r={5 * k} fill={color} />)}
          </>
        )}

        {/* Fleche / ligne en cours (gere le style courbe) */}
        {draftDrag && (draftDrag.type === 'arrow' && draftDrag.arrowStyle === 'curved' ? (
          (() => {
            const c = curveControl(draftDrag.a, draftDrag.b)
            return (
              <path d={`M ${X(draftDrag.a.x)} ${Y(draftDrag.a.y)} Q ${X(c.x)} ${Y(c.y)} ${X(draftDrag.b.x)} ${Y(draftDrag.b.y)}`}
                fill="none" stroke={draftDrag.color} strokeWidth={4 * k} strokeLinecap="round"
                markerEnd={`url(#arrow-${colorId(draftDrag.color)})`} opacity={0.85} />
            )
          })()
        ) : (
          <line x1={X(draftDrag.a.x)} y1={Y(draftDrag.a.y)} x2={X(draftDrag.b.x)} y2={Y(draftDrag.b.y)}
            stroke={draftDrag.color} strokeWidth={4 * k} strokeLinecap="round"
            strokeDasharray={(draftDrag.type === 'line' || draftDrag.arrowStyle === 'dashed') ? `${12 * k},${7 * k}` : undefined}
            markerEnd={`url(#arrow-${colorId(draftDrag.color)})`} opacity={0.85} />
        ))}

        {/* Ligne tactique en cours */}
        {draftTactical && draftTactical.linkedHalos.length > 0 && (
          <polyline
            points={draftTactical.linkedHalos.map(id => haloAnchor(haloById.get(id))).filter(Boolean).map(p => `${X(p.x)},${Y(p.y)}`).join(' ')}
            fill="none" stroke={draftTactical.color} strokeWidth={3 * k} strokeDasharray={`${8 * k},${6 * k}`} opacity={0.85} />
        )}

        {/* Overlay de selection : le contour epouse la forme de l'annotation.
            Les poignees sont visuelles ; le hit-test est fait en code. */}
        {selectedAnn && (() => {
          const a = selectedAnn
          const hs = handlesOf(a)
          const handles = hs.map((h, i) => (
            <circle key={i} cx={h.x} cy={h.y} r={7} fill={BLUE} stroke="#fff" strokeWidth={2} />
          ))
          const outline = (() => {
            if (a.type === 'halo') {
              const anchor = haloAnchor(a)
              const cx = X(anchor.x), cy = Y(anchor.y), rx = haloRx(a)
              // Meme ellipse que le halo, 8 unites plus large : `haloRy` garde
              // le rapport d'aplatissement exact. Un `ry` en dur ici et le
              // contour de selection cesserait d'epouser la forme des que le
              // coach redimensionne.
              return <ellipse cx={cx} cy={cy} rx={rx + 8} ry={haloRy(rx + 8)} fill="none" stroke={BLUE} strokeWidth={2} strokeDasharray="6,4" />
            }
            if (a.type === 'arrow' && a.arrow_style === 'curved') {
              const p0 = a.points[0], p1 = a.points[1]
              const c = a.control || curveControl(p0, p1)
              return <path d={`M ${X(p0.x)} ${Y(p0.y)} Q ${X(c.x)} ${Y(c.y)} ${X(p1.x)} ${Y(p1.y)}`}
                fill="none" stroke={BLUE} strokeWidth={5} strokeDasharray="6,4" opacity={0.6} strokeLinecap="round" />
            }
            if (a.type === 'arrow' || a.type === 'line') {
              const [p0, p1] = a.points
              return <line x1={X(p0.x)} y1={Y(p0.y)} x2={X(p1.x)} y2={Y(p1.y)}
                stroke={BLUE} strokeWidth={5} strokeDasharray="6,4" opacity={0.6} strokeLinecap="round" />
            }
            if (a.type === 'zone') {
              return <polygon points={a.points.map(p => `${X(p.x)},${Y(p.y)}`).join(' ')}
                fill="none" stroke={BLUE} strokeWidth={2} strokeDasharray="6,4" />
            }
            if (a.type === 'tactical_line') {
              const pts = (a.linkedHalos || []).map(id => haloAnchor(haloById.get(id))).filter(Boolean)
              if (pts.length < 2) return null
              const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${X(p.x)} ${Y(p.y)}`).join(' ') + ' Z'
              return <path d={d} fill="none" stroke={BLUE} strokeWidth={5} strokeDasharray="6,4" opacity={0.6} />
            }
            // texte : bounding box + 4 poignees coin
            const b = bbox(a, haloById)
            if (!b) return null
            return <rect x={X(b.x0) - 12} y={Y(b.y0) - 12} width={X(b.x1 - b.x0) + 24} height={Y(b.y1 - b.y0) + 24}
              fill="none" stroke={BLUE} strokeWidth={2} strokeDasharray="6,4" />
          })()
          // Poignees coin pour le texte (bbox)
          let cornerHandles = null
          if (a.type === 'text') {
            const b = bbox(a, haloById)
            if (b) {
              const xs = [X(b.x0) - 12, X(b.x1) + 12], ys = [Y(b.y0) - 12, Y(b.y1) + 12]
              cornerHandles = xs.flatMap((cx, i) => ys.map((cy, j) => (
                <circle key={`c${i}${j}`} cx={cx} cy={cy} r={7} fill={BLUE} stroke="#fff" strokeWidth={2} />
              )))
            }
          }
          return <g pointerEvents="none">{outline}{handles}{cornerHandles}</g>
        })()}

        </g>{/* fin data-chrome */}
      </svg>

      {/* Input texte (positionne en %) */}
      {textDraft && (
        <input
          autoFocus
          value={textDraft.value}
          onChange={e => setTextDraft(d => ({ ...d, value: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitText() } else if (e.key === 'Escape') setTextDraft(null) }}
          // Correction : tout est selectionne a l'ouverture. Le cas courant est
          // de remplacer un libelle faux, pas d'y ajouter un mot.
          onFocus={e => { if (textDraft.id) e.target.select() }}
          placeholder={textDraft.id
            ? 'Libellé... (vide pour l\'effacer)'
            : 'Texte... (Entrée pour valider)'}
          style={{
            position: 'absolute', left: `${textDraft.x * 100}%`, top: `${textDraft.y * 100}%`,
            transform: 'translate(-50%, -50%)', zIndex: 7,
            fontFamily: DISP, fontSize: 16, fontWeight: 700, textAlign: 'center', color: '#fff',
            background: 'rgba(0,0,0,0.6)', border: `1px solid ${BLUE}`, borderRadius: 4,
            padding: '4px 8px', outline: 'none', minWidth: 120,
          }}
        />
      )}

      {/* Toolbar (haut centre) */}
      <div style={{
        position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap',
        maxWidth: isMobile ? 'calc(100% - 20px)' : 'none', justifyContent: 'center',
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
        borderRadius: 8, padding: 5, border: '1px solid rgba(255,255,255,0.1)',
      }}>
        {/* Selection / deplacement (actif par defaut) */}
        <button className="ano-btn" aria-label="Sélection / déplacement" aria-pressed={tool === 'select'}
          onClick={() => selectTool('select')} style={toolBtn(tool === 'select')}>
          <MousePointer2 size={16} />
        </button>
        <div style={divider} />

        {/* Halo */}
        <button className="ano-btn" aria-label="Halo joueur" aria-pressed={tool === 'halo'}
          onClick={() => selectTool('halo')} style={toolBtn(tool === 'halo')}>
          <User size={16} />
        </button>

        {/* Dropdown Fleche (3 styles) */}
        <div ref={arrowMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button className="ano-btn" aria-label="Flèche" aria-pressed={tool === 'arrow'}
            onClick={() => selectTool('arrow')} style={{ ...toolBtn(tool === 'arrow'), paddingRight: 0 }}>
            <ArrowStyleIcon size={16} />
          </button>
          <button className="ano-btn" aria-label="Choisir le style de flèche" aria-expanded={arrowMenuOpen}
            onClick={() => setArrowMenuOpen(v => !v)}
            style={{ ...toolBtn(tool === 'arrow'), width: 16, padding: 0, borderRadius: 5 }}>
            <ChevronDown size={11} />
          </button>
          {arrowMenuOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 8,
              background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: 4,
              display: 'flex', flexDirection: 'column', gap: 2, minWidth: 150,
            }}>
              {ARROW_STYLES.map(s => {
                const SIcon = s.icon
                const active = tool === 'arrow' && arrowStyle === s.id
                return (
                  <button key={s.id} className="ano-btn" aria-pressed={active} onClick={() => chooseArrowStyle(s.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 9px', borderRadius: 6,
                      cursor: 'pointer', background: active ? BLUE : 'transparent', color: '#fff', border: 'none',
                      fontFamily: BODY, fontSize: 12, whiteSpace: 'nowrap', minHeight: 40, textAlign: 'left',
                    }}>
                    <SIcon size={15} /> {s.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Ligne / Zone / Texte */}
        {SIMPLE_TOOLS.slice(1).map(t => {
          const Icon = t.icon
          return (
            <button key={t.id} className="ano-btn" aria-label={t.label} aria-pressed={tool === t.id}
              onClick={() => selectTool(t.id)} style={toolBtn(tool === t.id)}>
              <Icon size={16} />
            </button>
          )
        })}
        <div style={divider} />
        {/* Ligne tactique (pattern signature) */}
        <button className="ano-btn" aria-label="Ligne tactique" aria-pressed={tool === 'tactical_line'}
          onClick={() => selectTool('tactical_line')} style={toolBtn(tool === 'tactical_line')}>
          <Triangle size={16} />
        </button>
        <div style={divider} />
        {COLORS.map(c => (
          <button key={c} className="ano-btn" aria-label={`Couleur ${c}`} onClick={() => setColor(c)}
            style={{
              width: isMobile ? 22 : 24, height: isMobile ? 22 : 24, borderRadius: '50%', flexShrink: 0,
              background: c, cursor: 'pointer', padding: 0,
              border: color === c ? '2px solid #fff' : '2px solid rgba(255,255,255,0.25)',
              boxShadow: color === c ? '0 0 0 1px rgba(0,0,0,0.4)' : 'none',
            }}
          />
        ))}
        <div style={divider} />
        <button className="ano-btn" aria-label="Effacer la sélection" onClick={deleteSelected}
          disabled={!selectedId} style={{ ...toolBtn(false), opacity: selectedId ? 1 : 0.4, cursor: selectedId ? 'pointer' : 'not-allowed' }}>
          <Trash2 size={16} />
        </button>
      </div>

      {/* Banniere d'aide Ligne tactique (sous la toolbar) */}
      {tool === 'tactical_line' && (
        <div style={{
          position: 'absolute', top: isMobile ? 74 : 55, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
          background: 'rgba(79,142,247,0.15)', border: '0.5px solid #4F8EF7', borderRadius: 5,
          padding: '6px 12px', color: BLUE, fontFamily: BODY, fontSize: 11, fontWeight: 600,
          textAlign: 'center', whiteSpace: isMobile ? 'normal' : 'nowrap',
          maxWidth: 'calc(100% - 20px)', boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        }}>
          Clique successivement sur les halos à relier · Entrée pour tracer · Esc pour annuler
        </div>
      )}

      {/* Bouton "Terminer" flottant (zone en cours, surtout mobile) */}
      {draftPoly.length >= 3 && (
        <button onClick={finishZone} className="ano-btn"
          style={{
            position: 'absolute', top: isMobile ? 66 : 56, left: '50%', transform: 'translateX(-50%)', zIndex: 7,
            ...actionBtn('primary'),
          }}>
          <Check size={15} /> Terminer la zone
        </button>
      )}
      {draftTactical && draftTactical.linkedHalos.length >= 2 && (
        <button onClick={finishTactical} className="ano-btn"
          style={{
            position: 'absolute', top: isMobile ? 116 : 92, left: '50%', transform: 'translateX(-50%)', zIndex: 7,
            ...actionBtn('primary'),
          }}>
          <Check size={15} /> Terminer la ligne
        </button>
      )}

      {/* Erreur d'enregistrement — dans l'overlay, au-dessus de la barre
          d'actions. Surtout pas un toast en haut a droite : le coach regarde
          son dessin, pas le coin de son ecran. C'est exactement ce qui a rendu
          le bug d'origine invisible pendant 5 PR. */}
      {saveError && (
        <div
          role="alert"
          style={{
            position: 'absolute', left: '50%', bottom: isMobile ? 128 : 88,
            transform: 'translateX(-50%)', zIndex: 8,
            maxWidth: 'calc(100% - 20px)',
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'rgba(226,75,74,0.96)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
            padding: '10px 12px', boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
          }}
        >
          <AlertCircle size={16} style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: BODY, fontSize: 12, lineHeight: 1.4 }}>
            {saveError}
          </span>
          <button
            className="ano-btn"
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 11px', borderRadius: 7, minHeight: 40,
              background: 'rgba(255,255,255,0.16)', color: '#fff',
              border: '0.5px solid rgba(255,255,255,0.4)',
              fontFamily: BODY, fontSize: 12, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <RotateCw size={13} /> Réessayer
          </button>
        </div>
      )}

      {/* Bottom overlay */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 5,
        background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 55%, rgba(0,0,0,0.95) 100%)',
        padding: '35px 10px 8px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Pencil size={13} color={BLUE} />
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.2em', textTransform: 'uppercase', color: BLUE, fontWeight: 700 }}>
            Mode annotation
          </span>
          <span style={{ fontFamily: BODY, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>· frame figée</span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="ano-btn" aria-label="Annuler (Ctrl+Z)" onClick={undo} disabled={undoStack.length === 0}
            style={{ ...smallBtn, opacity: undoStack.length ? 1 : 0.4, cursor: undoStack.length ? 'pointer' : 'not-allowed' }}>
            <Undo2 size={14} />
          </button>
          <button className="ano-btn" aria-label="Rétablir (Ctrl+Y)" onClick={redo} disabled={redoStack.length === 0}
            style={{ ...smallBtn, opacity: redoStack.length ? 1 : 0.4, cursor: redoStack.length ? 'pointer' : 'not-allowed' }}>
            <Redo2 size={14} />
          </button>
          <button className="ano-btn" aria-label="Effacer tout" onClick={clearAll} style={actionBtn('secondary')}>
            <Trash2 size={14} /> {isMobile ? '' : 'Effacer tout'}
          </button>
          <button className="ano-btn" aria-label="Exporter en PNG" onClick={exportPNG} style={actionBtn('secondary')}>
            <Download size={14} /> {isMobile ? '' : 'Export PNG'}
          </button>
          {/* spacer : pousse Enregistrer a droite (Quitter est dans le header) */}
          <div style={{ flex: 1, minWidth: 4 }} />
          <button className="ano-btn" aria-label="Enregistrer" onClick={handleSave} disabled={saving} style={{ ...actionBtn('primary'), opacity: saving ? 0.7 : 1 }}>
            <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}
